import { Component, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { gql } from "@apollo/client/core";
import {
  CustomDetailComponent,
  DataService,
  NotificationService,
  SharedModule,
} from "@vendure/admin-ui/core";
import { Asset } from "@vendure/common/lib/generated-types";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import { map } from "rxjs/operators";

const GET_ELIGIBLE_CERTIFICATIONS = gql`
  query GetEligibleCertifications {
    eligibleCertifications {
      id
      preview
      source
    }
  }
`;

const UPDATE_PRODUCT_CERTIFICATIONS = gql`
  mutation UpdateProductCertifications($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      id
    }
  }
`;

const GET_PRODUCT_CERTIFICATIONS = gql`
  query GetProductCertifications($id: ID!) {
    product(id: $id) {
      id
      customFields {
        certifications {
          id
        }
      }
    }
  }
`;

@Component({
  templateUrl: "./certification-selector.component.html",
  styleUrls: ["./certification-selector.scss"],
  standalone: true,
  imports: [SharedModule],
})
export class ProductCertificationsComponent
  implements CustomDetailComponent, OnInit
{
  entity$: Observable<any>;
  detailForm: FormGroup;

  selectedIds$ = new BehaviorSubject<string[]>([]);
  originalIds$ = new BehaviorSubject<string[]>([]);
  isSaving$ = new BehaviorSubject<boolean>(false);
  isLoading$ = new BehaviorSubject<boolean>(true);
  allCertifications$ = new BehaviorSubject<Asset[]>([]);

  selectedCertifications$: Observable<Asset[]>;
  hasChanges$: Observable<boolean>;

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.dataService
      .query(GET_ELIGIBLE_CERTIFICATIONS)
      .mapSingle((data: any) => data.eligibleCertifications || [])
      .subscribe((certifications) => {
        this.allCertifications$.next(certifications);
      });

    // this.entity$
    //   .pipe(
    //     tap((product) => {
    //       const certificationsIds =
    //         product?.customFields?.certificationsIds || [];
    //       this.selectedIds$.next(certificationsIds);
    //       this.originalIds$.next([...certificationsIds]);
    //       this.isLoading$.next(false);
    //     }),
    //   )
    //   .subscribe();

    this.dataService
      .query(GET_PRODUCT_CERTIFICATIONS, {
        id: this.route.snapshot.paramMap.get("id"),
      })
      .mapSingle((data) => data)
      .subscribe((result: any) => {
        const certifications = result?.product?.customFields?.certifications;
        if (certifications && certifications.length > 0) {
          const ids = certifications.map((cert) => cert.id);
          this.selectedIds$.next(ids);
          this.originalIds$.next([...ids]);
          this.isLoading$.next(false);
        }
      });

    this.selectedCertifications$ = combineLatest([
      this.allCertifications$,
      this.selectedIds$,
    ]).pipe(
      map(([allCerts, selectedIds]) =>
        allCerts.filter((cert) => selectedIds.includes(cert.id.toString())),
      ),
    );

    this.hasChanges$ = combineLatest([
      this.selectedIds$,
      this.originalIds$,
    ]).pipe(
      map(([current, original]) => {
        if (current.length !== original.length) return true;
        return !current.every((id) => original.includes(id));
      }),
    );
  }

  toggleCertification(certificationId: string) {
    if (this.isSaving$.value) return;

    const currentIds = this.selectedIds$.value;
    const index = currentIds.indexOf(certificationId);

    if (index > -1) {
      this.selectedIds$.next(currentIds.filter((id) => id !== certificationId));
    } else {
      this.selectedIds$.next([...currentIds, certificationId]);
    }
  }

  selectAll() {
    if (this.isSaving$.value) return;

    const allCerts = this.allCertifications$.value;
    this.selectedIds$.next(allCerts.map((cert) => cert.id.toString()));
  }

  clearAll() {
    if (this.isSaving$.value) return;
    this.selectedIds$.next([]);
  }

  isSelected(certificationId: string): boolean {
    return this.selectedIds$.value.includes(certificationId);
  }

  async saveCertifications() {
    console.log("here");
    try {
      const id = this.route.snapshot.paramMap.get("id");
      console.log("ID:", id);

      console.log("is saving bro");
      this.isSaving$.next(true);

      await this.dataService
        .mutate(UPDATE_PRODUCT_CERTIFICATIONS, {
          input: {
            id,
            customFields: {
              certificationsIds: this.selectedIds$.value,
            },
          },
        })
        .toPromise();

      this.originalIds$.next([...this.selectedIds$.value]);

      this.notificationService.success("Certifications updated successfully");
    } catch (error) {
      console.error("Error saving certifications:", error);
      this.notificationService.error("Failed to save certifications");
    } finally {
      this.isSaving$.next(false);
    }
  }
}
